import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import ApiService from '../../services/ApiService';
import { toast } from 'react-toastify';
import { formatDate, formatCurrency } from '../../utils/helper';
import { getAppointmentStatus } from '../../utils/statusHelper';
import AuthService from '../../services/AuthService';

const Details = () => {
    const { id } = useParams();
    const [isLoader, setIsLoader] = useState(false);
    const [appointment, setAppointment] = useState(null);
    const [status, setStatus] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const authUser = AuthService.getCurrentUser();

    useEffect(() => {
        getAppointment();
    }, [id]);

    const getAppointment = async () => {
        setIsLoader(true);
        try {
            const isCustomer = authUser?.role === 'customer';
            const url = isCustomer ? `customer/appointments/${id}` : `admin/appointments/${id}`;
            const response = await ApiService.request({
                method: 'GET',
                url: url,
            });
            if (response.data.status) {
                setAppointment(response.data.data);
                setStatus(response.data.data.status);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load appointment');
        } finally {
            setIsLoader(false);
        }
    };

    if (isLoader) return <Loader />;
    if (!appointment) return <div className="text-center p-5">Appointment not found</div>;

    const { badgeClass, label } = getAppointmentStatus(appointment.status);

    const handleAdminCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel this appointment?')) {
            return;
        }
        try {
            setIsUpdatingStatus(true);
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/appointments/${id}/cancel`,
            });
            if (response.data.status) {
                toast.success(response.data.message || 'Appointment cancelled successfully');
                await getAppointment();
            } else {
                toast.error(response.data.message || 'Failed to cancel appointment');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to cancel appointment');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleCustomerCancel = async () => {
        if (!window.confirm('Are you sure you want to cancel this appointment?')) {
            return;
        }
        try {
            setIsUpdatingStatus(true);
            const response = await ApiService.request({
                method: 'POST',
                url: `customer/appointments/${id}/cancel`,
            });
            if (response.data.status) {
                toast.success(response.data.message || 'Appointment cancelled successfully');
                await getAppointment();
            } else {
                toast.error(response.data.message || 'Failed to cancel appointment');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to cancel appointment');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleStatusUpdate = async () => {
        if (!status || status === appointment.status) {
            return;
        }
        try {
            setIsUpdatingStatus(true);
            const response = await ApiService.request({
                method: 'PUT',
                url: `admin/appointments/${id}`,
                data: { status },
            });
            if (response.data.status) {
                toast.success(response.data.message || 'Status updated successfully');
                await getAppointment();
            } else {
                toast.error(response.data.message || 'Failed to update status');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Appointment Details
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/appointments" className="text-muted text-hover-primary">Appointments</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                {appointment.ref_no || '-'}
                            </li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/appointments" className="btn btn-sm fw-bold btn-secondary">Back</Link>
                        {authUser?.role !== 'customer' && (
                            <Link to={`/appointments/edit/${id}`} className="btn btn-sm fw-bold btn-base">
                                Edit Appointment
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-body pt-9 pb-5">
                            <div className="row mb-7">
                                <div className="col-md-6">
                                    <div className="d-flex flex-column">
                                        <span className="text-muted fw-semibold fs-7">Reference No</span>
                                        <span className="fw-bold fs-5 text-gray-800">
                                            {appointment.ref_no || '-'}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                    <div className="d-flex flex-column align-items-md-end align-items-start gap-3">
                                        <span className={`badge ${badgeClass} fs-6 fw-bold`}>
                                            {label}
                                        </span>
                                        {authUser?.role !== 'customer' && (
                                            <div className="d-flex align-items-center gap-2">
                                                <select
                                                    className="form-select form-select-sm w-auto"
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="confirmed">Confirmed</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="cancelled">Cancelled</option>
                                                    <option value="no_show">No Show</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-base"
                                                    disabled={isUpdatingStatus || !status || status === appointment.status}
                                                    onClick={handleStatusUpdate}
                                                >
                                                    {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                                                </button>
                                                {appointment.status !== 'cancelled' && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        disabled={isUpdatingStatus}
                                                        onClick={handleAdminCancel}
                                                    >
                                                        Cancel Booking
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {authUser?.role === 'customer' && appointment.status !== 'cancelled' && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger mt-2"
                                                disabled={isUpdatingStatus}
                                                onClick={handleCustomerCancel}
                                            >
                                                Cancel Booking
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-10">
                                <div className="col-md-6">
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Date</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {formatDate(appointment.date)}
                                        </div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Time</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {appointment.time}
                                        </div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Price</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {formatCurrency(appointment.price)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Customer</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {appointment.customer?.name || '-'}
                                        </div>
                                        <div className="text-muted fs-7">
                                            {appointment.customer?.phone || ''}{appointment.customer?.email ? ` • ${appointment.customer.email}` : ''}
                                        </div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Branch</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {appointment.branch?.name || '-'}
                                        </div>
                                    </div>
                                    <div className="mb-5">
                                        <div className="text-muted fw-semibold fs-7 mb-1">Services</div>
                                        <div className="fw-bold fs-6 text-gray-800">
                                            {appointment.services && appointment.services.length > 0
                                                ? appointment.services.map(s => s.name).join(', ')
                                                : (appointment.service?.name || '-')}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {appointment.notes && (
                                <div className="mb-5">
                                    <div className="text-muted fw-semibold fs-7 mb-1">Notes</div>
                                    <div className="fw-semibold fs-6 text-gray-800">
                                        {appointment.notes}
                                    </div>
                                </div>
                            )}

                            {appointment.transactions && appointment.transactions.length > 0 && (
                                <div className="mt-10">
                                    <h3 className="fw-bold text-dark mb-5">Transaction History</h3>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Date</th>
                                                    <th>Type</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Transaction ID</th>
                                                    <th>Payment Method</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {appointment.transactions.map((tx) => (
                                                    <tr key={tx.id}>
                                                        <td>{formatDate(tx.created_at, 'MMM D, YYYY HH:mm')}</td>
                                                        <td className="text-capitalize">{tx.type.replace('_', ' ')}</td>
                                                        <td>{formatCurrency(tx.amount)}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${tx.status === 'success' ? 'success' : 'danger'}`}>
                                                                {tx.status}
                                                            </span>
                                                        </td>
                                                        <td>{tx.stripe_transaction_id || '-'}</td>
                                                        <td className="text-uppercase">{tx.payment_method || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Details;
